import { Link, useRouterState } from '@tanstack/react-router';
import { Logotipo } from '../brand/Logotipo.tsx';
import { pontosAExibir, useSincronizacao } from '../../state/sincronizacao.ts';
import { sequenciaActual, useEventos } from '../../state/eventos.ts';
import { mensagensPorLer, nomeDe, useIdentidade } from '../../state/identidade.ts';
import { useCursoActual } from '../../state/cursoActual.ts';
import { t } from '../../i18n/index.ts';
import './barra.css';

/**
 * A barra de topo persistente (à Mimo, pedido do dono a 16/08/2026): está em
 * todos os ecrãs do estudante EXCEPTO dentro de uma lição — a lição é um
 * túnel e a regra da casa proíbe navegação lateral lá dentro; é o router que
 * a esconde nesse caso, não este componente.
 *
 * Três estados:
 *   visitante  → logo · Aprender · Cursos · Preços · Entrar · Criar conta
 *   com rasto  → ganha os chips de pontos (◆) e sequência (▲) — os mesmos
 *                números do painel, derivados do mesmo registo de eventos
 *   com conta  → ganha o avatar com a inicial, que é a porta da conta
 */
export function BarraDeTopo({ comMarca = false }: { comMarca?: boolean }) {
  const identidade = useIdentidade((s) => s.identidade);
  const porLer = useIdentidade(mensagensPorLer);
  const eventos = useEventos((s) => s.eventos);
  const totais = useSincronizacao((s) => s.totais);
  // ADR 0027 — «Aprender» leva ao curso onde o estudante ia; sem curso
  // guardado, ao catálogo, que é onde se escolhe um.
  const cursoActual = useCursoActual((s) => s.trilha);

  // O destaque da aba decide-se pelo caminho, não pelo `activeProps` do Link:
  // o «Aprender» aponta para o curso **sem** a `?seccao=`, e o Link deixava de
  // se dar por activo assim que o estudante abria uma secção (a query deixava
  // de bater certo). Pelo caminho, «Aprender» acende em qualquer `/curso/…` e
  // «Cursos» só no catálogo.
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const naAprender = caminho.startsWith('/curso/');
  const noCatalogo = caminho === '/';

  const pontos = pontosAExibir(eventos, totais);
  const dias = sequenciaActual(eventos);
  const temRasto = pontos > 0 || dias > 0;

  const marca = (
    <Link to="/" className="barra__marca" aria-label={t('barra.inicio.aria')}>
      <Logotipo />
    </Link>
  );

  return (
    <header className="barra">
      {/* No catálogo a marca é o h1 da página; nos outros ecrãs o h1 é do
          conteúdo e a marca é só a ligação de regresso. */}
      {comMarca ? <h1 className="barra__titulo">{marca}</h1> : marca}

      <nav className="barra__nav" aria-label={t('barra.aria')}>
        {cursoActual ? (
          <Link
            to="/curso/$trackId"
            params={{ trackId: cursoActual }}
            search={{}}
            className={naAprender ? 'btn btn--ghost barra__activo' : 'btn btn--ghost'}
            aria-current={naAprender ? 'page' : undefined}
          >
            {t('barra.aprender')}
          </Link>
        ) : (
          <Link
            to="/"
            className={naAprender ? 'btn btn--ghost barra__activo' : 'btn btn--ghost'}
            aria-current={naAprender ? 'page' : undefined}
          >
            {t('barra.aprender')}
          </Link>
        )}
        <Link
          to="/"
          className={noCatalogo ? 'btn btn--ghost barra__activo' : 'btn btn--ghost'}
          aria-current={noCatalogo ? 'page' : undefined}
        >
          {t('barra.cursos')}
        </Link>
        <Link to="/precos" className="btn btn--ghost">
          {t('precos.titulo')}
        </Link>
        {identidade?.eTutor ? (
          <Link to="/turmas" className="btn btn--ghost">
            {t('tutor.nav')}
          </Link>
        ) : null}
        {identidade?.eCoordenador ? (
          <Link to="/instituicao" className="btn btn--ghost">
            {t('coordenador.nav')}
          </Link>
        ) : null}
      </nav>

      <div className="barra__lado">
        {temRasto ? (
          <span className="barra__chips">
            <span className="chip chip--destaque" aria-label={`${pontos} ${t('painel.pontos')}`}>
              <span aria-hidden="true">◆</span> {pontos}
            </span>
            <span
              className={dias > 0 ? 'chip chip--destaque' : 'chip'}
              aria-label={`${dias} ${t('painel.sequencia')}`}
            >
              <span aria-hidden="true">▲</span> {dias}
            </span>
          </span>
        ) : null}

        {identidade ? (
          <>
            {/* ADR 0028 — as mensagens, ao lado do perfil (pedido do dono):
                o envelope acende com o número por ler, e o número vive
                também no rótulo, para quem não vê. */}
            <Link
              to="/mensagens"
              className={porLer > 0 ? 'barra__mensagens barra__mensagens--ha' : 'barra__mensagens'}
              aria-label={porLer > 0 ? t('barra.mensagens.porLer', { n: porLer }) : t('barra.mensagens.aria')}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
              {porLer > 0 ? <span className="barra__contador" aria-hidden="true">{porLer > 9 ? '9+' : porLer}</span> : null}
            </Link>
            <Link to="/conta" className="barra__avatar" aria-label={t('barra.conta.aria')}>
              <span aria-hidden="true">{nomeDe(identidade.email).charAt(0).toUpperCase()}</span>
            </Link>
          </>
        ) : (
          <span className="barra__entrada">
            <Link to="/entrar" className="btn btn--ghost">
              {t('conta.entrar.titulo')}
            </Link>
            <Link to="/registar" className="btn btn--primary barra__cta">
              {t('conta.registar.titulo')}
            </Link>
          </span>
        )}
      </div>
    </header>
  );
}
